/**
 * Hybrid Heartbeat Engine — um único loop / scheduler.
 * - dirty + mudança real → BEHAVIOR_SUMMARY (snapshot completo, otimizações intactas)
 * - clean / safe_mode / reduced / snapshot igual → HEARTBEAT leve (presence realtime)
 */

import type { GomarkePixelConfig } from '../types';
import type { BehaviorSnapshot, HeartbeatPayload } from './types';
import { BehaviorPhase } from './types';
import { getScrollState } from './scroll';
import { getClickState } from './click';
import { getIdleState } from './idle';
import { getVisibilityState } from './visibility';
import { getMouseMovementCount, getKeypressCount } from './rateLimit';
import { getBehaviorStateMachine } from './stateMachine';
import { addBehaviorSnapshot, getBehaviorBatchPayload } from './buffer';
import { isBehaviorDirty, clearBehaviorDirty } from './dirty';
import {
  flushBehaviorBufferToQueue,
  enqueueHeartbeatEvent,
} from './queueBridge';
import {
  getHeartbeatIntervalMultiplier,
  shouldReduceBehaviorSampling,
} from '../core/backpressure';
import {
  getUnifiedPressureLevel,
  setBrowserLagHint,
} from '../core/runtime/pressureModel';
import { getRuntimeMode } from '../core/runtime/runtimeMode';
import { recordAdaptiveDecision } from '../core/telemetry';

function isDebugPayloadSize(): boolean {
  return (
    typeof window !== 'undefined' &&
    !!(window as unknown as { __behaviorDebug?: boolean }).__behaviorDebug
  );
}

const HEARTBEAT_INTERVAL_MS = 5000;
const DEFAULT_HEARTBEAT_ONLY_INTERVAL_MS = 30_000;
const DEFAULT_HIDDEN_HEARTBEAT_MIN_MS = 30_000;

let heartbeatIntervalId: ReturnType<typeof setInterval> | null = null;
let lastSnapshot: BehaviorSnapshot | null = null;
let lastHeartbeatAt = 0;
let heartbeatPresenceOnly = false;
let heartbeatGetVisitorId: (() => string | undefined) | null = null;
let heartbeatGetSessionId: (() => string | undefined) | null = null;
let heartbeatConfig: GomarkePixelConfig | null = null;
let heartbeatVersion: string = '1.0.0';

let heartbeatEnrichSnapshot:
  | ((s: BehaviorSnapshot) => BehaviorSnapshot)
  | null = null;

function getPageId(): string {
  if (typeof window === 'undefined') return '';
  return window.location.pathname || window.location.href || '';
}

function isPresenceHeartbeatEnabled(): boolean {
  return heartbeatConfig?.behavior?.presenceHeartbeatEnabled !== false;
}

function isHeartbeatOnly(): boolean {
  return heartbeatConfig?.behavior?.heartbeatOnly === true;
}

function isDocumentVisible(): boolean {
  if (typeof document === 'undefined') return true;
  return document.visibilityState !== 'hidden';
}

function getHeartbeatTickIntervalMs(): number {
  const configured = heartbeatConfig?.behavior?.heartbeatIntervalMs;
  if (
    typeof configured === 'number' &&
    Number.isFinite(configured) &&
    configured > 0
  ) {
    return configured;
  }
  if (isHeartbeatOnly()) return DEFAULT_HEARTBEAT_ONLY_INTERVAL_MS;
  return HEARTBEAT_INTERVAL_MS;
}

function buildPresenceState(): BehaviorSnapshot['state'] {
  const idle = getIdleState();
  const visibility = getVisibilityState();
  const phase = getBehaviorStateMachine().currentState;
  return {
    isActive: phase === BehaviorPhase.ACTIVE,
    isIdle: idle.isIdle,
    isVisible: visibility.isVisible,
  };
}

/** Payload mínimo — mesmo shape do snapshot, sem scroll/interaction/timing/engagement. */
export function buildMinimalHeartbeatPayload(
  visitorId: string,
  sessionId: string
): HeartbeatPayload {
  return {
    visitorId,
    sessionId,
    pageId: getPageId(),
    timestamp: Date.now(),
    state: buildPresenceState(),
  };
}

function snapshotEquals(a: BehaviorSnapshot, b: BehaviorSnapshot): boolean {
  const base =
    a.scroll.maxDepth === b.scroll.maxDepth &&
    a.interaction.clicks === b.interaction.clicks &&
    a.interaction.mouseMovements === b.interaction.mouseMovements &&
    a.interaction.keypresses === b.interaction.keypresses &&
    a.timing.activeTime === b.timing.activeTime &&
    a.timing.idleTime === b.timing.idleTime &&
    a.timing.visibleTime === b.timing.visibleTime &&
    a.state.isActive === b.state.isActive &&
    a.state.isIdle === b.state.isIdle &&
    a.state.isVisible === b.state.isVisible;
  const engagement =
    (a.engagement?.score ?? 0) === (b.engagement?.score ?? 0) &&
    (a.engagement?.level ?? 'low') === (b.engagement?.level ?? 'low');
  return base && engagement;
}

function buildSnapshot(
  visitorId: string,
  sessionId: string,
  enrichSnapshot?: (s: BehaviorSnapshot) => BehaviorSnapshot
): BehaviorSnapshot | null {
  const scroll = getScrollState();
  const click = getClickState();
  const idle = getIdleState();
  const visibility = getVisibilityState();
  const now = Date.now();

  const visibleTime =
    visibility.isVisible && visibility.visibleSince > 0
      ? now - visibility.visibleSince
      : 0;

  let snapshot: BehaviorSnapshot = {
    visitorId,
    sessionId,
    pageId: getPageId(),
    timestamp: now,
    scroll: {
      maxDepth: scroll.maxScrollPercent,
    },
    interaction: {
      clicks: click.clickCount,
      mouseMovements: getMouseMovementCount(),
      keypresses: getKeypressCount(),
    },
    timing: {
      activeTime: idle.activeMs,
      idleTime: idle.idleMs,
      visibleTime,
    },
    state: buildPresenceState(),
  };
  if (enrichSnapshot) snapshot = enrichSnapshot(snapshot);
  return snapshot;
}

function shouldThrottlePresenceHeartbeat(now: number): boolean {
  if (isHeartbeatOnly()) return false;
  const visibility = getVisibilityState();
  const useHiddenInterval = heartbeatPresenceOnly || !visibility.isVisible;
  if (!useHiddenInterval) return false;
  const minInterval =
    heartbeatConfig?.behavior?.hiddenHeartbeatMinIntervalMs ??
    DEFAULT_HIDDEN_HEARTBEAT_MIN_MS;
  return now - lastHeartbeatAt < minInterval;
}

function tryEnqueuePresenceHeartbeat(
  visitorId: string,
  sessionId: string
): boolean {
  if (!isPresenceHeartbeatEnabled() || !heartbeatConfig) return false;
  if (isHeartbeatOnly() && !isDocumentVisible()) return false;
  const now = Date.now();
  if (shouldThrottlePresenceHeartbeat(now)) return false;
  enqueueHeartbeatEvent(
    heartbeatConfig,
    heartbeatVersion,
    visitorId,
    sessionId,
    buildMinimalHeartbeatPayload(visitorId, sessionId)
  );
  lastHeartbeatAt = now;
  return true;
}

function flushBehaviorSnapshot(visitorId: string, sessionId: string): boolean {
  if (!isBehaviorDirty()) return false;
  const snapshot = buildSnapshot(
    visitorId,
    sessionId,
    heartbeatEnrichSnapshot ?? undefined
  );
  if (!snapshot) return false;

  if (lastSnapshot !== null && snapshotEquals(lastSnapshot, snapshot)) {
    clearBehaviorDirty();
    return false;
  }
  lastSnapshot = snapshot;
  addBehaviorSnapshot(snapshot);
  clearBehaviorDirty();
  if (isDebugPayloadSize()) {
    const payload = getBehaviorBatchPayload(visitorId, sessionId);
    const size = JSON.stringify(payload).length;
    console.log(
      '[Behavior] Payload total (batch):',
      size,
      'bytes (~',
      (size / 1024).toFixed(1),
      'KB)'
    );
  }
  if (heartbeatConfig) {
    flushBehaviorBufferToQueue(
      heartbeatConfig,
      heartbeatVersion,
      visitorId,
      sessionId
    );
    return true;
  }
  return false;
}

function tickHybridHeartbeat(visitorId: string, sessionId: string): void {
  if (!isPresenceHeartbeatEnabled() && !isBehaviorDirty()) return;

  const reduced = shouldReduceBehaviorSampling();
  const safeMode = getRuntimeMode() === 'safe_mode';

  if (heartbeatPresenceOnly || reduced || safeMode) {
    if (reduced || safeMode) {
      recordAdaptiveDecision(
        'heartbeat',
        reduced ? 'presence_only_reduced' : 'presence_only_safe_mode'
      );
    }
    tryEnqueuePresenceHeartbeat(visitorId, sessionId);
    return;
  }

  if (isBehaviorDirty()) {
    const sent = flushBehaviorSnapshot(visitorId, sessionId);
    if (!sent) tryEnqueuePresenceHeartbeat(visitorId, sessionId);
    return;
  }

  tryEnqueuePresenceHeartbeat(visitorId, sessionId);
}

/** Flush imediato (hidden, exit intent, beforeunload). Ignora dirty e igualdade. Enfileira na Queue para o beacon do unload enviar. */
export function flushBehaviorSnapshotNow(
  visitorId: string,
  sessionId: string
): void {
  if (isHeartbeatOnly()) return;
  const snapshot = buildSnapshot(
    visitorId,
    sessionId,
    heartbeatEnrichSnapshot ?? undefined
  );
  if (!snapshot) return;
  lastSnapshot = snapshot;
  addBehaviorSnapshot(snapshot);
  clearBehaviorDirty();
  if (isDebugPayloadSize()) {
    const payload = getBehaviorBatchPayload(visitorId, sessionId);
    console.log(
      '[Behavior] Payload total (flush now):',
      JSON.stringify(payload).length,
      'bytes'
    );
  }
  if (heartbeatConfig) {
    flushBehaviorBufferToQueue(
      heartbeatConfig,
      heartbeatVersion,
      visitorId,
      sessionId
    );
  }
}

/** Aba hidden: só HEARTBEAT leve (throttle), sem parar o intervalo. */
export function setHeartbeatPresenceOnlyMode(enabled: boolean): void {
  if (isHeartbeatOnly()) {
    heartbeatPresenceOnly = true;
    return;
  }
  heartbeatPresenceOnly = enabled;
}

export function startBehaviorHeartbeat(
  getVisitorId: () => string | undefined,
  getSessionId: () => string | undefined,
  enrichSnapshot?: (s: BehaviorSnapshot) => BehaviorSnapshot,
  config?: GomarkePixelConfig,
  version?: string
): void {
  if (typeof window === 'undefined') return;
  stopBehaviorHeartbeat();
  heartbeatGetVisitorId = getVisitorId;
  heartbeatGetSessionId = getSessionId;
  heartbeatEnrichSnapshot = enrichSnapshot ?? null;
  heartbeatConfig = config ?? null;
  heartbeatVersion = version ?? '1.0.0';
  if (isHeartbeatOnly()) {
    heartbeatPresenceOnly = true;
  }
  const unified = getUnifiedPressureLevel();
  const pressureScale = unified >= 0.85 ? 2 : unified >= 0.6 ? 1.5 : 1;
  const interval =
    getHeartbeatTickIntervalMs() *
    getHeartbeatIntervalMultiplier() *
    pressureScale;
  if (pressureScale > 1) {
    recordAdaptiveDecision('heartbeat', `interval_x${pressureScale}`);
  }
  let expectedTick = Date.now() + interval;
  heartbeatIntervalId = setInterval(() => {
    const now = Date.now();
    const drift = Math.max(0, now - expectedTick);
    setBrowserLagHint(Math.min(1, drift / Math.max(interval, 1)));
    expectedTick = now + interval;
    const visitorId = getVisitorId();
    const sessionId = getSessionId();
    if (!visitorId || !sessionId) return;
    tickHybridHeartbeat(visitorId, sessionId);
  }, interval);
}

export function stopBehaviorHeartbeat(): void {
  if (heartbeatIntervalId) {
    clearInterval(heartbeatIntervalId);
    heartbeatIntervalId = null;
  }
  lastSnapshot = null;
  lastHeartbeatAt = 0;
  heartbeatPresenceOnly = false;
}

/** Retoma heartbeat após pausar (ex.: voltar de aba hidden). */
export function resumeBehaviorHeartbeat(): void {
  if (heartbeatGetVisitorId && heartbeatGetSessionId) {
    setHeartbeatPresenceOnlyMode(false);
    startBehaviorHeartbeat(
      heartbeatGetVisitorId,
      heartbeatGetSessionId,
      heartbeatEnrichSnapshot ?? undefined,
      heartbeatConfig ?? undefined,
      heartbeatVersion
    );
  }
}

export function resetHeartbeatLastSnapshot(): void {
  lastSnapshot = null;
}

/** Reset de estado para testes. */
export function _testResetHeartbeatState(): void {
  stopBehaviorHeartbeat();
  heartbeatGetVisitorId = null;
  heartbeatGetSessionId = null;
  heartbeatEnrichSnapshot = null;
  heartbeatConfig = null;
  heartbeatVersion = '1.0.0';
  lastSnapshot = null;
  lastHeartbeatAt = 0;
  heartbeatPresenceOnly = false;
}
